import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { actionPriceService } from "@/api/services"
import type { ActionPriceResponseDto } from "@/api/types/actionPrice.types"
import { toast } from "sonner"
import Sidebar from "@/components/Sidebar"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { RichEditor, RichText, isRichEditorEmpty } from "@/components/ui/rich-editor"
import FileUpload from "@/components/FileUpload"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Loader2, ArrowLeft, Edit2, Trash2, Save } from "lucide-react"

export default function ActionPriceDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [actionPrice, setActionPrice] = useState<ActionPriceResponseDto | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")

  // Edit states
  const [isEditing, setIsEditing] = useState(false)
  const [formData, setFormData] = useState({
    description: "",
    costCredits: "",
    durationDays: "",
    isActive: true,
    iconUploadedFileId: "",
  })
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  useEffect(() => {
    if (id) {
      loadActionPrice()
    }
  }, [id])

  const loadActionPrice = async () => {
    if (!id) return
    setLoading(true)
    setError("")
    try {
      const data = await actionPriceService.getActionPriceById(id)
      setActionPrice(data)
      setFormData({
        description: data.description || "",
        costCredits: data.costCredits.toString(),
        durationDays: data.durationDays.toString(),
        isActive: data.isActive,
        iconUploadedFileId: data.iconUploadedFileId || "",
      })
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || err.message || "Failed to load action price"
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = () => {
    setIsEditing(true)
  }

  const handleCancelEdit = () => {
    setIsEditing(false)
    if (actionPrice) {
      setFormData({
        description: actionPrice.description || "",
        costCredits: actionPrice.costCredits.toString(),
        durationDays: actionPrice.durationDays.toString(),
        isActive: actionPrice.isActive,
        iconUploadedFileId: actionPrice.iconUploadedFileId || "",
      })
    }
  }

  const handleSave = async () => {
    if (
      !id ||
      !formData.costCredits ||
      isNaN(Number(formData.costCredits)) ||
      Number(formData.costCredits) < 0
    ) {
      setError("Valid cost credits is required")
      return
    }

    if (
      !formData.durationDays ||
      isNaN(Number(formData.durationDays)) ||
      Number(formData.durationDays) < 0
    ) {
      setError("Valid duration days is required")
      return
    }

    setSaving(true)
    setError("")
    setSuccess("")

    try {
      const updated = await actionPriceService.updateActionPrice(id, {
        description: isRichEditorEmpty(formData.description) ? undefined : formData.description,
        costCredits: Number(formData.costCredits),
        durationDays: Number(formData.durationDays),
        isActive: formData.isActive,
        iconUploadedFileId: formData.iconUploadedFileId.trim() || undefined,
      })
      setActionPrice(updated)
      setSuccess("Action price updated successfully")
      setIsEditing(false)
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || err.message || "Failed to update action price"
      setError(errorMessage)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!id) return

    setDeleting(true)
    setError("")
    setDeleteDialogOpen(false)

    try {
      await actionPriceService.deleteActionPrice(id)
      navigate("/action-prices")
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || err.message || "Failed to delete action price"
      setError(errorMessage)
      setDeleting(false)
    }
  }

  if (loading) {
    return (
      <div className="flex">
        <Sidebar />
        <main className="flex-1 ml-64 p-8 bg-background min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </main>
      </div>
    )
  }

  if (!actionPrice) {
    return (
      <div className="flex">
        <Sidebar />
        <main className="flex-1 ml-64 p-8 bg-background min-h-screen">
          <div className="max-w-2xl">
            <Card className="p-6">
              <div className="text-center">
                <p className="text-destructive mb-4">{error || "Action price not found"}</p>
                <Button onClick={() => navigate("/action-prices")} variant="outline">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Action Prices
                </Button>
              </div>
            </Card>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="flex">
      <Sidebar />

      <main className="flex-1 ml-64 p-8 bg-background min-h-screen">
        <div className="max-w-7xl">
          <div className="flex items-center gap-4 mb-8">
            <Button variant="ghost" size="sm" onClick={() => navigate("/action-prices")}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Action Prices
            </Button>
            <h1 className="text-3xl font-bold text-foreground">Action Price Details</h1>
          </div>

          {/* Success message */}
          {success && (
            <div className="bg-success/10 border border-success/30 rounded-lg p-3 mb-6">
              <p className="text-sm text-success">{success}</p>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3 mb-6">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {/* Action Price Information */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-foreground">
                Action Price Information
              </h2>
              {!isEditing && (
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={handleEdit}>
                    <Edit2 className="mr-2 h-4 w-4" />
                    Edit
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setDeleteDialogOpen(true)}
                    disabled={deleting}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </Button>
                </div>
              )}
            </div>

            {isEditing ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="costCredits">
                      Cost Credits <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="costCredits"
                      type="number"
                      step="1"
                      min="0"
                      value={formData.costCredits}
                      onChange={(e) =>
                        setFormData({ ...formData, costCredits: e.target.value })
                      }
                      onKeyDown={(e) => e.key === "Enter" && handleSave()}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="durationDays">
                      Duration (Days) <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="durationDays"
                      type="number"
                      step="1"
                      min="0"
                      value={formData.durationDays}
                      onChange={(e) =>
                        setFormData({ ...formData, durationDays: e.target.value })
                      }
                      onKeyDown={(e) => e.key === "Enter" && handleSave()}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Description</Label>
                  <RichEditor
                    value={formData.description}
                    onChange={(html) => setFormData({ ...formData, description: html })}
                    disabled={saving}
                    placeholder="Write a description..."
                  />
                </div>
                <div className="space-y-2">
                  <FileUpload
                    label="Icon"
                    description="Upload an icon for this action price"
                    value={formData.iconUploadedFileId || undefined}
                    onChange={(fileId) =>
                      setFormData({ ...formData, iconUploadedFileId: fileId || "" })
                    }
                    onDeleteSuccess={async () => {
                      // Update action price to set iconUploadedFileId to null
                      if (id && isEditing && actionPrice) {
                        try {
                          // Include current values to ensure valid update payload
                          // Send undefined to omit the field (axios will omit undefined values)
                          const updatePayload = {
                            description: actionPrice.description,
                            costCredits: actionPrice.costCredits,
                            durationDays: actionPrice.durationDays,
                            isActive: actionPrice.isActive,
                            // Omit iconUploadedFileId to clear it (or set to undefined)
                            iconUploadedFileId: undefined,
                          }
                          
                          await actionPriceService.updateActionPrice(id, updatePayload)
                          // Update form data
                          setFormData({ ...formData, iconUploadedFileId: "" })
                          // Reload action price to get updated data
                          await loadActionPrice()
                        } catch (err: any) {
                          console.error("Update action price error:", {
                            status: err.response?.status,
                            data: err.response?.data,
                            message: err.message,
                          })
                          const errorMessage =
                            err.response?.data?.message ||
                            err.message ||
                            "Failed to update action price"
                          setError(errorMessage)
                          toast.error(errorMessage)
                        }
                      } else {
                        // If not editing, just clear the form field
                        setFormData({ ...formData, iconUploadedFileId: "" })
                      }
                    }}
                    previewSize="md"
                    disabled={saving}
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="isActive"
                      checked={formData.isActive}
                      onChange={(e) =>
                        setFormData({ ...formData, isActive: e.target.checked })
                      }
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    <Label htmlFor="isActive" className="cursor-pointer">
                      Active
                    </Label>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button onClick={handleSave} disabled={saving}>
                    {saving ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="mr-2 h-4 w-4" />
                        Save
                      </>
                    )}
                  </Button>
                  <Button variant="outline" onClick={handleCancelEdit} disabled={saving}>
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-muted-foreground">Category</Label>
                    <p className="text-foreground font-medium">{actionPrice.category}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Action Type</Label>
                    <div className="mt-1">
                      <Badge variant="outline">{actionPrice.actionType}</Badge>
                    </div>
                  </div>
                  <div className="col-span-2">
                    <Label className="text-muted-foreground">Description</Label>
                    {actionPrice.description ? <RichText html={actionPrice.description} /> : <p>—</p>}
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Cost Credits</Label>
                    <p className="text-foreground font-medium">{actionPrice.costCredits}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Duration (Days)</Label>
                    <p className="text-foreground font-medium">
                      {actionPrice.durationDays}
                    </p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Status</Label>
                    <div className="mt-1">
                      <Badge variant={actionPrice.isActive ? "default" : "secondary"}>
                        {actionPrice.isActive ? "Active" : "Inactive"}
                      </Badge>
                    </div>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Icon File ID</Label>
                    <p className="text-foreground font-medium text-sm break-all">
                      {actionPrice.iconUploadedFileId || "N/A"}
                    </p>
                  </div>
                  {actionPrice.iconUploadedFile && (
                    <div className="col-span-2">
                      <Label className="text-muted-foreground">Icon</Label>
                      <div className="mt-2">
                        <img
                          src={actionPrice.iconUploadedFile.url}
                          alt={actionPrice.iconUploadedFile.name}
                          className="h-16 w-16 object-contain border rounded"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          {actionPrice.iconUploadedFile.name}
                        </p>
                      </div>
                    </div>
                  )}
                  <div>
                    <Label className="text-muted-foreground">Created At</Label>
                    <p className="text-foreground font-medium">
                      {new Date(actionPrice.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Updated At</Label>
                    <p className="text-foreground font-medium">
                      {new Date(actionPrice.updatedAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </Card>
        </div>
      </main>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Action Price</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this action price? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
